/**
 * Reads the human-readable message out of a failed API call.
 *
 * The backend does not answer errors with ProblemDetails. `HttpStatusExtension.ToActionResult()`
 * builds a **bare string** — `Error.Title + "." + Error.Detail` — and ASP.NET writes it as
 * `text/plain`, so Angular hands it back as a plain string on `HttpErrorResponse.error`. Code that
 * reaches for `err.error.title`, `err.error.detail` or `err.error.error.detail` therefore reads
 * `undefined` off a string and silently falls through to its own generic fallback: the server said
 * exactly what was wrong and the user was told "something went wrong".
 *
 * This handles the string shape first and still understands the object shapes, so it keeps working
 * if an endpoint ever does return ProblemDetails.
 *
 * @param err     The error an `HttpClient` subscription received.
 * @param fallback Shown when the response carried no usable message.
 */
export function apiErrorMessage(err: unknown, fallback: string): string {
    const body = (err as { error?: unknown } | null | undefined)?.error;

    if (typeof body === 'string') {
        const text = body.trim();
        // Title and Detail are concatenated with a "." that lands next to the title's own full stop,
        // giving "Range Too Large..That window would...". Collapse exactly that pair, never an ellipsis.
        return text ? text.replace(/(?<!\.)\.\.(?!\.)\s*/g, '. ').trim() : fallback;
    }

    if (body && typeof body === 'object') {
        const shape = body as { detail?: unknown; title?: unknown; message?: unknown; error?: unknown };
        const nested = shape.error as { detail?: unknown; title?: unknown; message?: unknown } | undefined;
        const candidate = shape.detail ?? shape.title ?? shape.message
            ?? nested?.detail ?? nested?.title ?? nested?.message;
        if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
    }

    return fallback;
}
